import { fn } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
// @ts-expect-error: Incorrect type
import InfiniteScroller from '@zestia/ember-simple-infinite-scroller/components/infinite-scroller';
import { t } from 'ember-intl';
import LoadingBar from 'lumen/components/general/loading-bar';
import ParityInsightBreakdownBlockTitle from 'lumen/components/parity/insight/breakdown-block-title';
import ParityInsightBreakdownValue from 'lumen/components/parity/insight/breakdown-value';
import ParityInsightBreakdownValueLoading from 'lumen/components/parity/insight/breakdown-value-loading';
import {
  type Breakdown,
  PA_SORT_TYPES,
  type ParityFilter,
} from 'lumen/utils/constants/pi-constant-values';
import {
  calculateParityAnalyticsValues,
  isParityObjectEmpty,
} from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import {
  canAddParityFilter,
  limitReached,
} from 'lumen/utils/parity-analytics/can-add-parity-filter';
import segmentationValueFormatter from 'lumen/utils/parity-analytics/pa-segmentation-value-formatter';

import styles from './breakdown-block.module.css';

// Number of results needed to start slicing the results
const MIN_SLICE_LENGTH = 50;
// Length of items rendered at a time
const SLICE_LENGTH = 25;
// Max amounts of rows in a breakdown ("Other" row excluded)
const NB_OF_BREAKDOWN_ITEMS_TO_DISPLAY = 5;

interface ParityInsightBreakdownBlockSignature {
  Args: {
    hideHotelsMetric: boolean;
    isLoading?: boolean;
    parsedSegmentationFilters: Record<string, string>;
    redirectToTrends: (segmentationName: Breakdown) => void;
    searchValues?: {
      isSearchActive: boolean;
      searchValue: string;
    };
    segmentationBlockIdx: number;
    segmentationData?: {
      segmentationName: string;
      segmentationObjects: unknown[];
    };
    segmentationFilterClicked: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    setIsSearchActive: (segmentationName: Breakdown, isActive: boolean) => void;
    setSearchValue: (segmentationName: Breakdown, searchValue: string) => void;
    showLoadingPlaceholderState: boolean;
    sortKey: ParityFilter;
    sortType: string;
  };
  Element: HTMLDivElement;
}

export default class ParityInsightBreakdownBlock extends Component<ParityInsightBreakdownBlockSignature> {
  @service declare intl: Services['intl'];

  @tracked displayedSegmentationObjectsIndex = SLICE_LENGTH;
  @tracked isOtherRowCurrentlyExpanded = false;

  elScrollContainer = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allSegmentationObjects() {
    // @ts-expect-error: Incorrect type
    let visibleSegmentations = [];
    const createOtherRow =
      this.sortedSegmentationObjects?.length > 5 && !this.isSearchActive;

    // Keep reference to other row here
    const otherRowConfig = {
      isOtherRow: true,
      id: 'other',
      isSelectable: false,
      isExpanded: this.isOtherRowCurrentlyExpanded,
      segmentation_id: 'other',
      segmentation_value: null,
      segmentation_value_type: 'other',
    };

    // Loop over sortedSegmentationObjects as rows
    this.sortedSegmentationObjects.some((row, index) => {
      // If user entered something in the block's search field...
      if (this.isSearchActive) {
        // Reset row
        // @ts-expect-error: Incorrect type
        row.parent = null;
        // If row matches, add it anyway
        // @ts-expect-error: Incorrect type
        if (this.isSearchMatch(row.segmentation_value, row.segmentation_id)) {
          visibleSegmentations.push(row);
        }
        // if there are children, find the matching ones
        // @ts-expect-error: Incorrect type
        if (row.children) {
          // @ts-expect-error: Incorrect type
          const matchingChildren = [];
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          row.children.forEach((segmentationChild) => {
            if (
              this.isSearchMatch(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                segmentationChild.segmentation_value,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                segmentationChild.segmentation_id,
              )
            ) {
              matchingChildren.push(segmentationChild);
            }
          });
          if (matchingChildren.length) {
            // if matches are found, set expanded
            // @ts-expect-error: Incorrect type
            row.isExpanded = true;
            visibleSegmentations = [
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...visibleSegmentations,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...matchingChildren,
            ];
          } else {
            // @ts-expect-error: Incorrect type
            row.isExpanded = false;
          }
        }
      } else {
        // OPTIMIZE notes for making this parent-child list generic
        // Should listend to segmentation_value_type for parenting
        // Should look up associated children, and throw them under the parent within the same iteration
        // Should Generate an other row in a more functional manner to hide ugly code from this place
        // Should set the depth of rows in the nested list as properties which CSS should fix, while keeping the list flat (virtually nested)

        // If currently at the 0-indexed position of the 'other' row, and there are more rows to come...
        if (index === NB_OF_BREAKDOWN_ITEMS_TO_DISPLAY - 1 && createOtherRow) {
          // Set the children property of the 'other' row to contain the remaining items of the list up until now
          const otherChildSegmentationObjects =
            this.sortedSegmentationObjects.slice(index);
          // Make sure these children get this 'other' row set as parent (this indents them visually)
          this.setParentForChildren(
            otherRowConfig,
            otherChildSegmentationObjects,
          );
          // @ts-expect-error: Incorrect type
          otherRowConfig.children = otherChildSegmentationObjects;

          // Calculate average of the new children as the value for the other row
          const results = this.calculateTotalValuesOfSegmentationObjects(
            otherChildSegmentationObjects,
          );
          // @ts-expect-error: Incorrect type
          otherRowConfig.calculated_values = results.calculatedValues;
          // @ts-expect-error: Incorrect type
          otherRowConfig.value = results.value;

          // Add the other row as a visible row in the list
          visibleSegmentations.push(otherRowConfig);

          if (otherRowConfig.isExpanded) {
            // If expanded, should show all the 'other' row's children directly underneath
            visibleSegmentations = [
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...visibleSegmentations,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...otherRowConfig.children,
            ];
          }

          // @ts-expect-error: Incorrect type
          otherRowConfig.segmentation_value = this.intl
            .t('insight_pages.breakdown.more', {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              count: otherRowConfig.children.length,
            })
            .toString();

          // Returning true -> exit loop
          return true;
        }

        // @ts-expect-error: Incorrect type
        if (row.parent) {
          // In some cases i.e. rows get assigned as child to a parent, but by filtering this can change...
          // When not explicitly child we check to delete the parent property to avoid indentation issues
          // @ts-expect-error: Incorrect type
          delete row.parent;
        }

        // If no 'other' row active, we didn't reach the limit yet. This is the default case, just add.
        visibleSegmentations.push(row);

        // Support 1-level deep parent/children nesting
        // @ts-expect-error: Incorrect type
        if (row.isExpanded) {
          // @ts-expect-error: Incorrect type
          this.setParentForChildren(row, row.children);
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
          visibleSegmentations = [...visibleSegmentations, ...row.children];
        }
      }

      // Must always return a value in a .some()
      return false;
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return visibleSegmentations;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get arrSegmentationObjects() {
    return this.args.segmentationData?.segmentationObjects;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get blockDisplayName() {
    return this.intl.t(`parity_analytics.${this.segmentationName}`, {
      count: 1,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledTooltipMessage() {
    if (
      // @ts-expect-error: Incorrect type
      limitReached(this.args.parsedSegmentationFilters, this.segmentationName)
    ) {
      return this.intl.t(
        'parity_insight.filtering_unavailable.explanation.only_1',
      );
    }

    return this.intl.t(
      'parity_insight.filtering_unavailable.explanation.maximum_reached',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayedSegmentationObjects() {
    // Optimize search scenario
    if (this.allSegmentationObjects.length > MIN_SLICE_LENGTH) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.allSegmentationObjects.slice(
        0,
        this.displayedSegmentationObjectsIndex,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.allSegmentationObjects;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    return !canAddParityFilter(
      // @ts-expect-error: Incorrect type
      this.args.parsedSegmentationFilters,
      this.segmentationName,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchActive() {
    return this.args.searchValues?.isSearchActive;
  }

  // Find the max number of shops in one of the segmentation values
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxNbOfShops() {
    const segmentations = this.allSegmentationObjects;
    let max = 0;

    segmentations.forEach((segmentation) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      let currentValue = segmentation.calculated_values.totalNbOfShops;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (segmentation.isOtherRow) {
        let maxFromChildren = 0;

        // Find max in all children instead of other row value
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        segmentation.children.forEach((child) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (child.calculated_values.totalNbOfShops > maxFromChildren) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            maxFromChildren = child.calculated_values.totalNbOfShops;
          }
        });

        currentValue = maxFromChildren;
      }

      // Is the currently found maximum value bigger than maxBlock?
      if (currentValue > max) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        max = currentValue;
      }
    });

    return max;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValue() {
    return this.args.searchValues?.searchValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationName() {
    return this.args.segmentationData?.segmentationName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationObjects() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const selectedSegmentationValues =
      this.args.parsedSegmentationFilters[
        // @ts-expect-error: Incorrect type
        this.args.segmentationData?.segmentationName
      ];
    const filteredBreakdownKeys = Object.keys(
      this.args.parsedSegmentationFilters,
    );

    if (this.arrSegmentationObjects && this.arrSegmentationObjects.length) {
      // Don't show null values for non-filtered breakdowns
      const filteredSegmentationObjects = this.arrSegmentationObjects.filter(
        (segObj) => {
          const isBreakdownFiltered =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            filteredBreakdownKeys.indexOf(segObj.segmentation_id) !== -1;
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const isEmpty = isParityObjectEmpty(segObj.value);
          if (!isBreakdownFiltered && isEmpty) {
            return false;
          }
          return true;
        },
      );

      return filteredSegmentationObjects.sort((a, b) => {
        // Sorting by selected state yes/no. And within those two groups by loss, meet, and win.
        // Determining which of the two is selected.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isSelectedA =
          selectedSegmentationValues &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          selectedSegmentationValues.indexOf(a.segmentation_value_id) !== -1;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isSelectedB =
          selectedSegmentationValues &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          selectedSegmentationValues.indexOf(b.segmentation_value_id) !== -1;

        // Transforming the selected state into a number used by the sort function
        const sortA = isSelectedA ? 0 : 1;
        const sortB = isSelectedB ? 0 : 1;

        // If either one selected when the other is not, we sort according to selection state
        if (sortA !== sortB) {
          return sortA - sortB;
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const sortType = PA_SORT_TYPES[this.args.sortType];

        // If both are selected or unselected, then we sort according to the loss, meet, win of the values
        if (
          this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b[sortType],
            this.args.sortKey,
          ) !== 0
        ) {
          return this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b[sortType],
            this.args.sortKey,
          );
        }
        if (
          // @ts-expect-error: Incorrect type
          a.unfiltered_data &&
          // @ts-expect-error: Incorrect type
          b.unfiltered_data &&
          this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a.unfiltered_data[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b.unfiltered_data[sortType],
            this.args.sortKey,
          ) !== 0
        ) {
          // If comparing normal filtered values results equal, compare win/meet/loss of the unfiltered data
          return this.compareWinMeetLoss(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            a.unfiltered_data[sortType],
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            b.unfiltered_data[sortType],
            this.args.sortKey,
          );
        }

        // As a last resort, compare IDs
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return a.id.localeCompare(b.id);
      });
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get titleParams() {
    return {
      segmentationName: this.segmentationName,
      isLoading: this.args.showLoadingPlaceholderState,
      searchValues: this.args.searchValues,
      blockDisplayName: this.blockDisplayName,
      updateSearchValue: this.updateSearchValue.bind(this),
      updateIsSearchActive: this.updateIsSearchActive.bind(this),
    };
  }

  willDestroy(): void {
    super.willDestroy();

    this.elScrollContainer = null;
  }

  // => we need to calculate the values for the Other row ourselves (total values of all the child segmentations)
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateTotalValuesOfSegmentationObjects(segmentationObjects) {
    const accumulatedValues = {
      win: 0,
      meet: 0,
      loss: 0,
      subscriptions: 0,
      nr_of_shops: 0,
    };

    // Loop over all segmentation objects and accumulate their win/meet/loss/shops
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    segmentationObjects.forEach((segmentationObj) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      Object.keys(segmentationObj.value).forEach((key) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        accumulatedValues[key] += segmentationObj.value[key];
      });
    });

    return {
      value: accumulatedValues,
      calculatedValues: calculateParityAnalyticsValues(accumulatedValues),
    };
  }

  // Will return [..., -1], 0, [1, ...] based on comparing loss, then meet, then win.
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareWinMeetLoss(a, b, key) {
    // Default sorting order
    const sortingOrder = ['loss', 'meet', 'win'];

    // Change sorting order by selection i.e.: [meet, loss, win] if key is 'meet'
    // 1. Remove key 2. Readd key as first elementof array
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sortingOrder.splice(sortingOrder.indexOf(key), 1);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sortingOrder.splice(0, 0, key);

    // Three-level sorting based on the sortingOrder array
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (a[sortingOrder[0]] !== b[sortingOrder[0]]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return b[sortingOrder[0]] - a[sortingOrder[0]];
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (a[sortingOrder[1]] !== b[sortingOrder[1]]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return b[sortingOrder[1]] - a[sortingOrder[1]];
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return b[sortingOrder[2]] - a[sortingOrder[2]];
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isSearchMatch(segmentationValue, segmentationId) {
    const valueFormattedLowerCase = segmentationValueFormatter(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      segmentationValue,
      this.intl,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      segmentationId,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.store,
    )
      .toString()
      .toLowerCase();
    return (
      // @ts-expect-error: Incorrect type
      valueFormattedLowerCase.indexOf(this.searchValue.toLowerCase()) !== -1
    );
  }

  // Sets the parent property to point to a specific parent row on a collection of children rows
  // @ts-expect-error: Incorrect type
  setParentForChildren(parent, children): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    children.forEach((child) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      child.parent = parent;
    });
  }

  // Sorting function which expects an object containing { win, meet, loss } values
  // @ts-expect-error: Incorrect type
  updateIsSearchActive(isSearchActive): void {
    if (!isSearchActive) {
      this.resetIndex();
      // Collapse the segmentations again when stopping the search
      // TODO: this is a side effect on a computed property
      // This is should not be done this way
      this.displayedSegmentationObjects.forEach((segmentationValue) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        segmentationValue.isExpanded = false;
      });
    }
    if (this.elScrollContainer) {
      // @ts-expect-error: Incorrect type
      this.elScrollContainer.scrollTop = 0;
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.setIsSearchActive(this.segmentationName, isSearchActive);
  }

  // helper function to calculate the total values of all passed segmentation objects in the array
  // @ts-expect-error: Incorrect type
  updateSearchValue(searchValue): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.setSearchValue(this.segmentationName, searchValue);
  }

  @action
  // @ts-expect-error: Incorrect type
  didInsertInfiniteScroll(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.elScrollContainer = element;
  }

  @action
  expandPopup(): void {
    // @ts-expect-error: Incorrect type
    this.args.redirectToTrends(this.segmentationName);
  }

  @action
  loadMore(): void {
    const currentSearchIndexText = this.displayedSegmentationObjectsIndex;
    // Stop loading more if index is greater than the number of results
    if (currentSearchIndexText < this.allSegmentationObjects?.length) {
      this.displayedSegmentationObjectsIndex =
        currentSearchIndexText + SLICE_LENGTH;
    }
  }

  @action
  resetIndex(): void {
    this.displayedSegmentationObjectsIndex = SLICE_LENGTH;
  }

  @action
  // @ts-expect-error: Incorrect type
  setIsOtherRowCurrentlyExpanded(value): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.isOtherRowCurrentlyExpanded = value;
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleSearch(isEnabled): void {
    this.updateIsSearchActive(isEnabled);
  }

  @action
  triggerScrollbarUpdate(hasToScrollDown = false): void {
    if (hasToScrollDown) {
      // We scroll down a bit so the user can see that the segmentation value is expanded and that he can scroll down in the blocks
      setTimeout(() => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.elScrollContainer.scrollBy({
          top: 120,
          behavior: 'smooth',
        });
      });
    }
  }

  <template>
    {{#if @isLoading}}
      <PrismCard @contentLayout="noPadding">
        <:header>
          <LoadingBar />
        </:header>
        <:content>
          <div class={{styles.container}} />
        </:content>
      </PrismCard>
    {{else}}
      <div ...attributes>
        <PrismCard
          @contentLayout="noPadding"
          class="parity relative overflow-hidden"
        >
          <:header as |Header|>
            <ParityInsightBreakdownBlockTitle
              {{! @glint-expect-error: Incorrect type }}
              @cardHeader={{Header}}
              @disabledTooltipMessage={{this.disabledTooltipMessage}}
              @isDisabled={{this.isDisabled}}
              {{! @glint-expect-error: Incorrect type }}
              @params={{this.titleParams}}
            />
          </:header>

          <:headerAfter>
            <div class="flex items-center gap-50">
              {{#if this.isSearchActive}}
                <PrismButton
                  @buttonType="ghost"
                  @icon="cross"
                  @onClick={{fn this.toggleSearch false}}
                />
              {{else}}
                <PrismButton
                  @buttonType="ghost"
                  @icon="search"
                  @onClick={{fn this.toggleSearch true}}
                  data-event-id="market-parity-insight-breakdown-search"
                  data-event-value={{this.segmentationName}}
                />
                <PrismButton
                  @buttonType="ghost"
                  @icon="expand"
                  @onClick={{this.expandPopup}}
                  data-event-id="market-parity-insight-breakdown-expand"
                  data-event-value={{this.segmentationName}}
                />
              {{/if}}
            </div>
          </:headerAfter>

          <:content>
            <div class={{styles.container}}>
              <InfiniteScroller
                @onLoadMore={{this.loadMore}}
                @percent={{85}}
                class={{styles.infinite-scroller}}
                {{didInsert this.didInsertInfiniteScroll}}
              >
                {{#if this.displayedSegmentationObjects.length}}
                  {{#each
                    this.displayedSegmentationObjects
                    as |segmentationValue|
                  }}
                    <ParityInsightBreakdownValue
                      {{! @glint-expect-error: Incorrect type }}
                      @blockKey={{@segmentationData.segmentationName}}
                      @data={{segmentationValue}}
                      @hideHotelsMetric={{@hideHotelsMetric}}
                      @isDisabled={{this.isDisabled}}
                      @isOtherRowCurrentlyExpanded={{this.isOtherRowCurrentlyExpanded}}
                      {{! @glint-expect-error: Incorrect type }}
                      @maxBlockValue={{this.maxBlockValue}}
                      @maxNbOfShops={{this.maxNbOfShops}}
                      @onSegmentationFilterClicked={{@segmentationFilterClicked}}
                      {{! @glint-expect-error: Incorrect type }}
                      @parsedSegmentationFilters={{@parsedSegmentationFilters}}
                      @resetIndex={{this.resetIndex}}
                      @segmentationBlockIdx={{@segmentationBlockIdx}}
                      @setIsOtherRowCurrentlyExpanded={{this.setIsOtherRowCurrentlyExpanded}}
                      @sortType={{@sortType}}
                      {{! @glint-expect-error: Incorrect type }}
                      @triggerScrollbarUpdate={{this.triggerScrollbarUpdate}}
                    />
                  {{/each}}
                {{else if @showLoadingPlaceholderState}}
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                  <ParityInsightBreakdownValueLoading />
                {{else}}
                  <p class="text-300--regular mt-200 ml-500">
                    {{t "general.no_results"}}
                  </p>
                {{/if}}
              </InfiniteScroller>
            </div>
          </:content>
        </PrismCard>
      </div>
    {{/if}}
  </template>
}
